"use client";

import { ReactNode } from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Lock, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import type { StepStatus } from "./types";

interface StepCardProps {
  stepNumber: number;
  title: string;
  description: string;
  status: StepStatus;
  completedSummary?: ReactNode;
  onGoBack?: () => void;
  children: ReactNode;
}

export function StepCard({
  stepNumber,
  title,
  description,
  status,
  completedSummary,
  onGoBack,
  children,
}: StepCardProps) {
  const isLocked = status === "locked";
  const isCompleted = status === "completed";
  const isProcessing = status === "in-progress";

  return (
    <Card
      className={cn(
        "transition-all duration-300",
        isLocked && "opacity-50 pointer-events-none",
        isCompleted && "border-green-500/30",
        isProcessing && "border-primary/50"
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold",
                isCompleted && "bg-green-500/20 text-green-500",
                isProcessing && "bg-primary/20 text-primary",
                status === "active" && "bg-primary/10 text-primary",
                isLocked && "bg-muted text-muted-foreground"
              )}
            >
              {isCompleted ? (
                <Check className="w-4 h-4" />
              ) : isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isLocked ? (
                <Lock className="w-3.5 h-3.5" />
              ) : (
                stepNumber
              )}
            </div>
            <div>
              <h3 className="font-semibold text-base">{title}</h3>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          {isCompleted && (
            <Badge variant="outline" className="text-green-500 border-green-500/30">
              Complete
            </Badge>
          )}
          {isProcessing && (
            <Badge className="bg-primary/20 text-primary border-primary/30">
              Processing
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isCompleted && completedSummary ? (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{completedSummary}</div>
            {onGoBack && (
              <Button variant="ghost" size="sm" onClick={onGoBack} className="ml-2 h-7 px-2 text-xs">
                <Pencil className="w-3 h-3 mr-1" />
                Edit
              </Button>
            )}
          </div>
        ) : (
          !isLocked && children
        )}
      </CardContent>
    </Card>
  );
}
